/*
LOCAL DEMO MODE ONLY - never reached by a deployed build.

Algolia has no emulator, so the search box cannot work offline. This stands in for
it, reading the demo content out of the local database and narrowing it by the same
keyword and facet filters the page would have sent to Algolia.

It is a plain substring match, not Algolia. The cloud function applies the same
rule in `functions/src/features/lms_export/helpers/local_search_helper.js` so that
what the page lists and what the random export draws from stay in step. The two
have to be changed together.
*/
import { isIterableArray } from '../../helpers/utils';

const facetSeparator = ':';
const searchableQuestionLabels = ['course', 'systems_areas', 'clinical_content', 'foundational_knowledge'];

/*
Filter attributes are dotted paths such as `question.course.label`, and any step
along the way can be an array, so every branch is followed.
*/
const getValuesAtPath = (record, path) => {
  let values = [record];

  path.split('.').forEach(segment => {
    const next = [];

    values.forEach(value => {
      const entries = isIterableArray(value) ? value : [value];
      entries.forEach(entry => {
        if (entry && typeof entry === 'object' && entry[segment] !== undefined) {
          next.push(entry[segment]);
        }
      });
    });

    values = next;
  });

  const flattened = [];
  values.forEach(value => (isIterableArray(value) ? flattened.push(...value) : flattened.push(value)));

  return flattened.filter(value => value !== null && typeof value !== 'undefined').map(String);
};

const matchesFacet = (record, facet) => {
  const separatorIndex = facet.indexOf(facetSeparator);
  if (separatorIndex < 0) {
    return true;
  }

  return getValuesAtPath(record, facet.substring(0, separatorIndex)).includes(facet.substring(separatorIndex + 1));
};

// one entry is an OR group, the entries together are an AND
const matchesFacetFilters = (record, facetFilters) => {
  if (!isIterableArray(facetFilters)) {
    return true;
  }

  return facetFilters.every(group =>
    isIterableArray(group) ? group.some(facet => matchesFacet(record, facet)) : matchesFacet(record, group)
  );
};

const getSearchableText = record => {
  const { question, testlet_information, patient_information } = record;
  const parts = [];

  const collect = value => typeof value === 'string' && parts.push(value);

  testlet_information && collect(testlet_information.testlet_title);

  if (patient_information) {
    ['about_patient', 'current_findings', 'patient_complaint', 'patient_history'].forEach(key =>
      collect(patient_information[key])
    );
  }

  if (question) {
    question.question_stem && collect(question.question_stem.text);
    isIterableArray(question.answers) &&
      question.answers.forEach(({ answer_choice, explanation }) => {
        answer_choice && collect(answer_choice.text);
        explanation && collect(explanation.text);
      });
    searchableQuestionLabels.forEach(key =>
      getValuesAtPath(record, 'question.' + key + '.label').forEach(collect)
    );
  }

  return parts.join(' ').toLowerCase();
};

const matchesQuery = (record, query) => {
  if (typeof query !== 'string' || query.trim() === '') {
    return true;
  }

  const text = getSearchableText(record);

  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(term => term !== '')
    .every(term => text.includes(term));
};

/*
The real index holds one record per question. The page shows one row per content,
which Algolia does with `distinct`, so the same collapsing happens here.
*/
const getSearchRecords = contentDocuments => {
  const records = [];

  contentDocuments.forEach(doc => {
    const contentData = doc.data();
    const { content_type, question, questions } = contentData;
    const contentQuestions = content_type === 'question' ? [question] : questions;

    isIterableArray(contentQuestions) &&
      contentQuestions.forEach(contentQuestion =>
        records.push({ ...contentData, question: contentQuestion, objectID: doc.id, testlet_id: doc.id })
      );
  });

  return records;
};

/*
Every filter the page offers becomes a facet except the publish status, which it
writes into the filters string as `is_published=1` or `=0`.
*/
const matchesPublishFilter = (record, filters) => {
  const match = typeof filters === 'string' ? filters.match(/is_published=([01])/) : null;

  return match === null || Boolean(record['is_published']) === (match[1] === '1');
};

export const searchLocalTestlets = (contentDocuments, query, params) => {
  const { facetFilters, filters } = params || {};
  const matched = getSearchRecords(contentDocuments).filter(
    record =>
      matchesQuery(record, query) && matchesFacetFilters(record, facetFilters) && matchesPublishFilter(record, filters)
  );

  const contentIds = new Set();

  return matched.filter(record => {
    if (contentIds.has(record.testlet_id)) {
      return false;
    }

    contentIds.add(record.testlet_id);
    return true;
  });
};
